import * as Phaser from "phaser";
import { assets, scenes } from "$/keys";
import { BasicScene } from "$/game/prefabs/scenes/BasicScene.ts";

type SceneValue<T> = T extends string ? T
  : T extends Record<string, unknown> ? SceneValue<T[keyof T]>
  : never;

type SceneKey = SceneValue<typeof scenes>;

interface TransitionData {
  showEntryAnimation?: boolean;
  transitionText?: string;
}

export class TransitionScenePlugin extends Phaser.Plugins.ScenePlugin {
  private transitionOverlay!: Phaser.GameObjects.Rectangle;
  private transitionText!: Phaser.GameObjects.Text;
  private eggSprite!: Phaser.GameObjects.Image;
  private isTransitioning = false;

  constructor(
    private myScene: BasicScene,
    pluginManager: Phaser.Plugins.PluginManager,
    pluginKey: string,
  ) {
    super(myScene, pluginManager, pluginKey);
  }

  override boot(): void {
    const emitter = this.systems?.events;
    if (!emitter) return;

    emitter.on("create", this.onCreate, this);
    emitter.once("destroy", this.sceneDestroy, this);
  }

  private onCreate(): void {
    this.isTransitioning = false;

    this.createTransitionOverlay();

    const data = this.myScene.scene.settings.data as TransitionData | undefined;
    if (data?.showEntryAnimation) {
      this.playEntryAnimation(data.transitionText || "LOADING...");
    } else {
      // Если нет анимации входа, сразу включаем ввод
      this.myScene.input.enabled = true;
    }
  }

  private createTransitionOverlay(): void {
    const cam = this.myScene.cameras.main;

    this.transitionOverlay = this.myScene.add.rectangle(
      cam.width / 2,
      cam.height / 2,
      cam.width,
      cam.height,
      parseInt(
        this.myScene.global.TUNER_TWA_CONFIG.headerColor.replace("#", "0x"),
        16,
      ),
    );
    this.transitionOverlay.setDepth(1000).setVisible(false);

    this.transitionText = this.myScene.add.text(
      cam.width / 2,
      cam.height / 2 + 60,
      "LOADING...",
      {
        fontSize: "40px",
        color: "#" +
          this.myScene.global.TUNER_TWA_CONFIG.palette.text.toString(16),
        fontFamily: "Phantom-Muff",
        fontStyle: "bold",
      },
    );

    this.transitionText.setOrigin(0.5).setDepth(1001).setVisible(false);

    this.eggSprite = this.myScene.add
      .image(0, 0, this.myScene.atlas, assets.sprites.egg.egg)
      .setScale(0.5)
      .setOrigin(0.5, 0.5)
      .setPosition(this.transitionText.x, this.transitionText.y - 125)
      .setVisible(false)
      .setDepth(1001);
  }

  private playEntryAnimation(message: string): void {
    this.isTransitioning = false;

    this.transitionOverlay.setVisible(true).setScale(1, 1).setOrigin(0, 0.5);
    this.transitionOverlay.x = 0;

    this.transitionText.setText(message).setVisible(true).setAlpha(1);
    this.eggSprite.setVisible(true);

    this.myScene.tweens.add({
      targets: this.transitionText,
      alpha: 0,
      duration: 300,
      delay: 100,
      onComplete: () => {
        this.transitionText.setVisible(false);
        this.myScene.soundManager.playSound(assets.sounds.whoosh, {
          volume: 0.25,
          start: 10,
        });
      },
    });

    this.myScene.tweens.add({
      targets: this.eggSprite,
      alpha: 0,
      duration: 300,
      delay: 100,
      onComplete: () => {
        this.eggSprite.setVisible(false);
      },
    });

    this.myScene.tweens.add({
      targets: this.transitionOverlay,
      scaleX: 0,
      duration: 300,
      delay: 100,
      ease: "Power2.easeInOut",
      onComplete: () => {
        this.transitionOverlay.setVisible(false);
        this.isTransitioning = false;
        this.myScene.input.enabled = true;
      },
    });
  }

  public toScene(
    sceneKey: SceneKey,
    transitionText: string = "LOADING...",
    duration = 1000,
  ): void {
    if (this.isTransitioning) return;
    this.isTransitioning = true;

    this.myScene.input.enabled = false;

    this.transitionText.setText(transitionText);
    this.myScene.soundManager.playSound(assets.sounds.whoosh, {
      volume: 0.5,
      start: 0.2,
    });

    this.closeScreen(() => {
      this.showTransitionText(() => {
        this.switchScene(sceneKey, transitionText);
      });
    });
  }

  private closeScreen(callback: () => void): void {
    this.transitionOverlay.setVisible(true).setOrigin(0, 0.5);
    this.transitionOverlay.x = 0;
    this.transitionOverlay.setScale(0, 1);

    this.myScene.tweens.add({
      targets: this.transitionOverlay,
      scaleX: 1,
      duration: 300,
      ease: "Power2.easeInOut",
      onComplete: callback,
    });
  }

  private showTransitionText(callback: () => void): void {
    this.transitionText.setVisible(true).setAlpha(0);
    this.eggSprite.setVisible(true).setAlpha(0);

    this.myScene.tweens.add({
      targets: this.transitionText,
      alpha: 1,
      duration: 300,
      onComplete: () => {
        this.myScene.time.delayedCall(100, callback);
      },
    });

    this.myScene.tweens.add({
      targets: this.eggSprite,
      alpha: 1,
      duration: 300,
      delay: 100,
      onComplete: () => {
        this.eggSprite.setVisible(true);
      },
    });
  }

  private switchScene(sceneKey: SceneKey, transitionText: string): void {
    this.isTransitioning = false;

    this.myScene.scene.start(sceneKey, {
      showEntryAnimation: true,
      transitionText,
    } as TransitionData);
  }

  public quickTransition(sceneKey: SceneKey): void {
    this.myScene.input.enabled = false;
    this.myScene.scene.start(sceneKey);
  }

  public setTransitionTextStyle(
    style: Phaser.Types.GameObjects.Text.TextStyle,
  ): void {
    this.transitionText.setStyle(style);
  }

  public setTransitionOverlayColor(color: number): void {
    this.transitionOverlay.setFillStyle(color);
  }

  private sceneDestroy(): void {
    this.transitionOverlay?.destroy();
    this.transitionText?.destroy();
    this.eggSprite?.destroy();
  }
}

// Пример регистрации плагина в конфигурации игры:
/*
const config: Phaser.Types.Core.GameConfig = {
  // ... другие настройки
  plugins: {
    scene: [
      {
        key: 'TransitionScenePlugin',
        plugin: TransitionScenePlugin,
        mapping: 'transition'
      }
    ]
  }
};

// Использование в сцене:
class MyScene extends Phaser.Scene {
  transition!: TransitionScenePlugin;

  create() {
    // Плагин автоматически инициализируется

    // Переход к другой сцене
    this.transition.transitionToScene('NextScene', 'LOADING NEXT...');

    // Быстрый переход
    this.transition.quickTransition('MenuScene');

    // Настройка стиля текста
    this.transition.setTransitionTextStyle({ fontSize: '50px', color: '#ffffff' });

    // Изменение цвета оверлея
    this.transition.setTransitionOverlayColor(0x000000);
  }
}
*/
